import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { FileType } from '@buildwise/pages/companyFiles/hooks/useFilesQuery';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import useUserData from 'hooks/useUserData';
import { uniqueId } from 'lodash';
import moment from 'moment';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PythonPostsAPI } from '../../api';
import { Box, Button, Typography } from '@mui/material';
import { ContentModal, CircularProgressWithLabel } from '@buildwise/libs/ui';
import { theme } from 'config/theme';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';

type WeWillWriteActionsProps = {
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function WeWillWriteActions({ setOpen }: WeWillWriteActionsProps) {
  const { user, companyId } = useUserData();
  const {
    formState: { isSubmitting },
    reset,
    setError,
    setValue,
    watch,
  } = useFormContext<PostFormData>();
  const attachments: AttachmentType[] = watch('attachments') || [];
  const [isLoading, setLoading] = useState(false);
  const [isCompleted, setCompleted] = useState(false);
  const [progressValues, setProgressValues] = useState<Record<string, number>>({});
  const avgProgressValue = useMemo(() => {
    const filesKeys = Object.keys(progressValues);
    return filesKeys.length > 0
      ? filesKeys.reduce((acc, key) => acc + progressValues[key], 0) / filesKeys.length
      : 0;
  }, [progressValues]);

  const handleSave = useCallback(async () => {
    if (attachments.length) {
      setLoading(true);
      setCompleted(false);
      let mappedFiles: FileType[] = [];
      mappedFiles = attachments.map((file) => ({
        createdAt: moment().toISOString(),
        createdBy: user?.username || '',
        documentDirectory: 'files',
        documentPath: 'files',
        documentName: file.fileName,
        documentPDF: '',
        documentSize: file.fileSize,
        documentType: file.fileName.split('.').pop() || '',
        documentURL: file.articleOpenUrl || '',
        id: uniqueId(),
        profileImageURL: user?.profileImageURL || '',
        userNameExpYearsToDisplay: user?.expYears || '',
        userNameTitleToDisplay: user?.title || '',
        userNameToDisplay: user?.userNameToDisplay || '',
        file: file.file,
        fileMD5Hash: file.fileMD5Hash,
      }));

      try {
        // Upload files meta info
        const { data } = await PythonPostsAPI.saveFilesMetaData({
          filesList: mappedFiles.map((file) => ({
            documentName: file.documentName,
            documentDirectory: file.documentDirectory,
            documentSize: file.documentSize,
            documentType: file.documentType,
            projectId: file.projectId,
          })),
          companyId,
          level: 'company',
          originIndicator: 'CREATE_POSTS',
        });

        // Upload files to S3
        mappedFiles = mappedFiles.map((file) => ({
          ...file,
          ...data.filesList.find((_file) => _file.documentName === file.documentName),
        }));
        const filesUploadPromises: Promise<any>[] = [];

        mappedFiles.forEach((file) => {
          filesUploadPromises.push(
            new Promise(async (resolve, reject) => {
              try {
                if (file.UploadUrl && file.file) {
                  await PythonPostsAPI.uploadFileToS3({
                    s3Url: file.UploadUrl,
                    file: file.file,
                    uploadProgress: (uploadedPercentage) => {
                      setProgressValues((prevState) => ({
                        ...prevState,
                        [file.documentName]: uploadedPercentage,
                      }));
                    },
                  });

                  // Verify uploaded files are saved
                  if (file.fileMD5Hash) {
                    await PythonPostsAPI.verifyUploadedFiles({
                      id: file.id,
                      companyId,
                      fileMD5Hash: file.fileMD5Hash,
                      MimeType: file.file.type,
                      filePath: file.s3Key || '',
                    });

                    resolve(true);
                  }
                }
              } catch (error) {
                console.log('S3_FILE_UPLOAD_ERROR', error);
                reject(error);
              }
            }),
          );
        });

        await Promise.all(filesUploadPromises);
        setCompleted(true);
        reset();
      } catch (error) {
        console.log('WE_WILL_WRITE_ERROR', error);
      }
    } else {
      setValue('attachments', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      setError(
        'attachments',
        { message: 'Please upload at least one file' },
        { shouldFocus: true },
      );
    }
  }, [attachments, user, companyId]);

  const onDismiss = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <Button variant="contained" disabled={isSubmitting} onClick={handleSave}>
        Submit
      </Button>

      {isLoading && (
        <ContentModal
          open={isLoading}
          content={
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              {isCompleted ? (
                <>
                  <CheckboxCircleFillIcon size={60} color={theme.palette.custom.greenBlue} />
                  <Typography
                    sx={(theme) => ({
                      my: 1,
                      color: theme.palette.custom.tuna,
                      fontWeight: 700,
                      fontSize: 16,
                    })}
                  >
                    Your {attachments.length > 1 ? 'files have been' : 'file is'} successfully
                    uploaded!
                  </Typography>
                  <Button variant="contained" onClick={onDismiss}>
                    Dismiss
                  </Button>
                </>
              ) : (
                <>
                  <CircularProgressWithLabel
                    sx={(theme) => ({
                      color: theme.palette.custom.pirateGold,
                      '& svg': { overflow: 'visible' },
                      '& svg circle': { strokeWidth: 5, strokeLinecap: 'round' },
                    })}
                    size={50}
                    value={avgProgressValue}
                  />
                  <Typography
                    sx={(theme) => ({
                      mt: 2,
                      color: theme.palette.custom.tuna,
                      fontWeight: 700,
                      fontSize: 16,
                    })}
                  >
                    `Uploading your ${attachments.length > 1 ? 'files' : 'file'}...`
                  </Typography>
                </>
              )}
            </Box>
          }
        />
      )}
    </>
  );
}

export default WeWillWriteActions;
